import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Work from './components/Work';
import About from './components/About';
import Contact from './components/Contact';
import Skill from './components/Skill';
import Stockpile from './components/Stockpile';
import Interface from './components/Interface';
import FlowGuard from './components/FlowGuard';
import ClinicFlow from './components/ClinicFlow'; // Import ClinicFlow
import BackgroundAccents from './components/BackgroundAccents';
import './styles/fonts.css';
import "@fontsource/montserrat"; // Import Montserrat font
import "@fontsource/montserrat/300.css"; // Light weight
import "@fontsource/montserrat/500.css"; // Medium weight
import "@fontsource/montserrat/700.css"; // Bold weight

const HomePage = () => {
  const navigate = useNavigate();

  const handleMenuClick = (item) => {
    console.log(`🎯 Menu clicked: ${item} - Navigating from / to ${item === 'WORK' ? '/work' : item === 'ABOUT' ? '/about' : '/contact'}`);
    if (item === 'WORK') {
      navigate('/work');
    } else if (item === 'ABOUT') {
      navigate('/about');
    } else if (item === 'CONTACT') {
      navigate('/contact');
    } else if (item === 'SKILL') {
      navigate('/skill');
    }
  };

  return (
    <div className="min-h-screen bg-vintage-beige font-sans selection:bg-black selection:text-white overflow-hidden">
      <BackgroundAccents />
      {/* Fixed Sidebar - Moved to Right */}
      <aside className="fixed right-0 top-0 w-80 h-screen flex flex-col justify-between z-50 pointer-events-none md:pointer-events-auto hidden md:flex" style={{ padding: '20px' }}>
        {/* Top: Social Icons */}
        <div className="flex flex-col items-end gap-6">
          <div className="flex flex-col gap-6">
            {/* LinkedIn Icon */}
            <a href="https://www.linkedin.com/in/aiman-rahimi-a78a91137/" target="_blank" rel="noopener noreferrer" className="group hover:opacity-60 transition-opacity">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" fill="currentColor" />
              </svg>
            </a>

            {/* Threads Icon */}
            <a href="https://www.threads.com/@aimanlati" target="_blank" rel="noopener noreferrer" className="group hover:opacity-60 transition-opacity">
              <svg width="20" height="20" viewBox="0 0 192 192" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 149.184 38.788C154.894 45.8136 159.199 54.6488 162.037 64.9503L178.184 60.6422C174.744 47.9622 169.331 37.0357 161.965 27.974C147.036 9.60668 125.202 0.195148 97.0695 0H96.9569C68.8816 0.19447 47.2921 9.6418 32.7883 28.0793C19.8819 44.4864 13.2244 67.3157 13.0007 95.9325L13 96L13.0007 96.0675C13.2244 124.684 19.8819 147.514 32.7883 163.921C47.2921 182.358 68.8816 191.806 96.9569 192H97.0695C122.03 191.827 139.624 185.292 154.118 170.811C173.081 151.866 172.51 128.119 166.26 113.541C161.776 103.087 153.227 94.5962 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C111.106 98.468 116.939 99.0737 122.242 100.233C120.264 124.935 108.662 128.946 98.4405 129.507Z" fill="currentColor"/>
              </svg>
            </a>
          </div>
          <div className="w-[1px] bg-black" style={{ height: '80px', marginTop: '20px' }}></div>
        </div>

        <div className="fixed right-8 bottom-8 z-50">
          <span
            className="text-black/40 text-[10px] font-sans tracking-widest block"
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
          >
            © 2025
          </span>
        </div>
      </aside>

      {/* Fixed Profile Info - Bottom Right */}
      <motion.div
        className="fixed right-24 md:right-32 bottom-10 z-40 hidden md:block font-sans text-right"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        <h2 className="text-sm font-bold tracking-widest text-black mb-6 uppercase">Aiman Rahimi</h2>
        <div className="space-y-6">
          <p className="text-sm text-gray-600 leading-relaxed max-w-[280px] ml-auto">
            Creative Developer / UI Craftsman /<br />
            Visual Problem Solver.
          </p>
          <p className="text-sm text-gray-500 leading-relaxed max-w-[280px] ml-auto">
            Focused on crafting digital experiences<br />
            where precision meets the "vibe."
          </p>
        </div>
      </motion.div>

      {/* Main Content Area - Fluid on mobile, offset on desktop */}
      <main className="md:pr-80 w-full min-h-screen flex md:items-center overflow-x-hidden">

        {/* Hero / Menu Section */}
        <section className="px-6 md:px-20 w-full pt-4 pb-20 md:py-0 flex flex-col md:block">
          {/* Mobile Top Bar - Icons, Line, Copyright */}
          <div className="md:hidden flex items-center justify-between mb-12 w-full">
            <div className="flex items-center gap-6">
              {/* LinkedIn */}
              <a href="https://www.linkedin.com/in/aiman-rahimi-a78a91137/" target="_blank" rel="noopener noreferrer" className="hover:opacity-60 transition-opacity">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" fill="currentColor" />
                </svg>
              </a>
              {/* Threads */}
              <a href="https://www.threads.com/@aimanlati" target="_blank" rel="noopener noreferrer" className="hover:opacity-60 transition-opacity">
                <svg width="18" height="18" viewBox="0 0 192 192" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 149.184 38.788C154.894 45.8136 159.199 54.6488 162.037 64.9503L178.184 60.6422C174.744 47.9622 169.331 37.0357 161.965 27.974C147.036 9.60668 125.202 0.195148 97.0695 0H96.9569C68.8816 0.19447 47.2921 9.6418 32.7883 28.0793C19.8819 44.4864 13.2244 67.3157 13.0007 95.9325L13 96L13.0007 96.0675C13.2244 124.684 19.8819 147.514 32.7883 163.921C47.2921 182.358 68.8816 191.806 96.9569 192H97.0695C122.03 191.827 139.624 185.292 154.118 170.811C173.081 151.866 172.51 128.119 166.26 113.541C161.776 103.087 153.227 94.5962 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C111.106 98.468 116.939 99.0737 122.242 100.233C120.264 124.935 108.662 128.946 98.4405 129.507Z" fill="currentColor"/>
                </svg>
              </a>
              {/* Horizontal Line */}
              <div className="h-[1px] bg-black w-24 opacity-20 ml-2"></div>
            </div>
            {/* Copyright */}
            <span className="text-[10px] tracking-widest text-black/40">© 2025</span>
          </div>

          {/* Mobile Profile Info - Appears only on mobile, at the top */}
          <motion.div
            className="md:hidden mb-12"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            <h2 className="text-sm font-bold tracking-widest text-black mb-4 uppercase">Aiman Rahimi</h2>
            <div className="space-y-4">
              <p className="text-sm text-gray-600 leading-relaxed">
                Creative Developer / UI Craftsman /<br />
                Visual Problem Solver.
              </p>
              <p className="text-sm text-gray-500 leading-relaxed">
                Focused on crafting digital experiences<br />
                where precision meets the "vibe."
              </p>
            </div>
          </motion.div>

          <nav className="flex flex-col items-start space-y-4 md:space-y-2">
            {['WORK', 'ABOUT', 'SKILL', 'CONTACT'].map((item) => (
              <motion.button
                key={item}
                className="relative group"
                onClick={() => handleMenuClick(item)}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                whileHover={{
                  x: 20,
                  transition: { type: "spring", stiffness: 400, damping: 10 }
                }}
              >
                <span
                  className="text-[18vw] sm:text-[14vw] md:text-[8rem] leading-[0.85] bonky-font text-black block tracking-tighter transition-all duration-300 group-hover:text-transparent"
                  style={{ WebkitTextStroke: '1px black' }}
                >
                  {item}
                </span>
                <span
                  className="absolute inset-0 text-[18vw] sm:text-[14vw] md:text-[8rem] leading-[0.85] bonky-font text-black block tracking-tighter opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{ transform: 'translate(4px, 4px)', color: 'rgba(0,0,0,0.1)' }}
                >
                  {item}
                </span>
              </motion.button>
            ))}
          </nav>
        </section>

      </main>
    </div>
  );
};

const pageVariants = {
  initial: {
    y: "-100vh",  // Start from top (like About page)
    opacity: 0
  },
  animate: {
    y: 0,  // Slide to normal position
    opacity: 1,
    transition: {
      duration: 0.8,  // Match About animation duration
      ease: [0.25, 0.46, 0.45, 0.94]  // Match About easing
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: "easeIn"
    }
  }
};



const PageWrapper = ({ children }) => {
  const location = useLocation();

  // Only show black background when on About page (not during transitions)
  const isAboutPage = location.pathname === '/about';

  React.useEffect(() => {
    console.log(`🛣️ Route Change: ${location.pathname} | Background: ${isAboutPage ? 'BLACK' : 'NORMAL'}`);
  }, [location.pathname]);

  return (
    <div className={`relative min-h-screen ${isAboutPage ? 'bg-black' : ''}`}>
      {children}
    </div>
  );
};

const AppWithRouter = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <PageWrapper>
              <motion.div
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                onAnimationStart={() => console.log('🏠 Home Animation START')}
                onAnimationComplete={() => console.log('✅ Home Animation COMPLETE')}
              >
                <HomePage />
              </motion.div>
            </PageWrapper>
          }
        />
        <Route
          path="/work"
          element={<Work />}
        />
        <Route
          path="/about"
          element={<About />}
        />
        <Route
          path="/contact"
          element={<Contact />}
        />
        <Route
          path="/skill"
          element={<Skill />}
        />
        <Route
          path="/stockpile"
          element={
            <Stockpile />
          }
        />
        <Route
          path="/interface"
          element={<Interface />}
        />
        <Route
          path="/flowguard"
          element={<FlowGuard />}
        />
        <Route
          path="/clinicflow"
          element={<ClinicFlow />}
        />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => {
  return (
    <Router>
      <AppWithRouter />
    </Router>
  );
};

export default App;